import {
    Drawer,
    DrawerBody,
    DrawerContent,
    DrawerHeader,
    DrawerOverlay,
    Flex,
    useToast,
} from '@chakra-ui/react';
import { Field, Form, Formik } from 'formik';
import { FiX } from 'react-icons/fi';
import * as yup from 'yup';

import { Button } from 'newui/components/Button';
import { RevisionRequestEmailContent } from 'components/email-template/RevisionRequestEmailContent';
import { requestRevision } from '../../api/revision-api';
import type { Rfx } from 'newui/features/Rfx/types/rfx.types';
import type { RfxSubmission } from '../../types/submission.type';
import { FormikInputWithLabel } from 'newui/components/ui/InputWithLabel';

export interface RequestRevisionDrawerProps {
    isOpen: boolean;
    rfxData: Rfx;
    rfxInviteData: RfxSubmission;
    onClose: () => void;
    onSuccess: () => void;
}

interface RequestRevisionFormValues {
    reason: string;
}

const validationSchema = yup.object().shape({
    reason: yup
        .string()
        .required('Reason is required')
        .max(2000, 'Reason must be at most 2000 characters'),
});

const RequestRevisionDrawer: React.FC<RequestRevisionDrawerProps> = ({
    isOpen,
    onClose,
    rfxData,
    rfxInviteData,
    onSuccess,
}) => {
    const toast = useToast();
    const rfxName = rfxData?.name ?? '';
    const companyName = rfxInviteData?.company?.name ?? '';
    const inviteId = rfxInviteData?.id;

    const initialValues: RequestRevisionFormValues = {
        reason: '',
    };

    const handleSubmit = async (
        values: RequestRevisionFormValues,
        { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
    ) => {
        try {
            await requestRevision(inviteId, { reason: values.reason, type: 'quote' });
            toast({
                title: 'Revision requested',
                description: 'A quote revision has been requested successfully.',
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
            onSuccess();
            onClose();
        } catch {
            toast({
                title: 'Error',
                description: 'Failed to request revision. Please try again.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Drawer
            placement="right"
            isOpen={isOpen}
            onClose={onClose}
            size="xl"
        >
            <DrawerOverlay onClick={onClose} />
            <DrawerContent maxWidth="1280px">
                <DrawerHeader className="border-b-2">
                    <Flex
                        justifyContent="space-between"
                        align="center"
                    >
                        <div>
                            <p className="text-xl font-semibold text-bg-brand-solid">
                                Request Quote Revision
                            </p>
                            <p className="text-sm font-normal text-gray-500">
                                Request the supplier to revise their quote submission. A
                                notification will be sent to the supplier.
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="rounded-lg p-2 hover:bg-gray-100"
                            aria-label="Close drawer"
                            data-testid="close-revision-drawer"
                        >
                            <FiX size={20} />
                        </button>
                    </Flex>
                </DrawerHeader>
                <DrawerBody>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        validateOnMount={false}
                    >
                        {({ isSubmitting, isValid, dirty, values }) => (
                            <Form>
                                <div className="grid grid-cols-3">
                                    <div className="col-span-1 pr-6">
                                        <p className="mb-4 text-lg font-semibold text-primary-900">
                                            Revision Request Details
                                        </p>
                                        <label className="mb-2 block text-sm font-medium text-gray-700">
                                            RFx: {rfxName}
                                        </label>
                                        <Field name="reason">
                                            {({
                                                field,
                                                meta,
                                            }: {
                                                field: React.InputHTMLAttributes<HTMLTextAreaElement> & { name: string };
                                                meta: { error?: string; touched: boolean; };
                                            }) => {
                                                // Omit `size` from field — HTML size (number) conflicts with Chakra size (string)
                                                const { size: _size, ...fieldProps } = field;
                                                return (
                                                    <FormikInputWithLabel label=' Reason for Revision' {...fieldProps} maxLength={200} type='textarea' />
                                                );
                                            }}
                                        </Field>
                                        <Flex
                                            gap={3}
                                            justify="flex-end"
                                        >
                                            <Button
                                                btntype="secondary"
                                                onClick={onClose}
                                                disabled={isSubmitting}
                                                data-testid="cancel-revision-btn"
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                type="submit"
                                                disabled={isSubmitting || !isValid || !dirty}
                                                isLoading={isSubmitting}
                                                data-testid="submit-revision-btn"
                                            >
                                                Request Revision
                                            </Button>
                                        </Flex>
                                    </div>
                                    <div className="col-span-2">
                                        <p className="ml-10 px-11 text-lg font-semibold text-primary-900">
                                            Email Preview
                                        </p>
                                        <div className="ml-10 px-11">
                                            <RevisionRequestEmailContent
                                                rfxName={rfxName}
                                                rfxLead={rfxData?.rfxLead?.fullName}
                                                reason={values.reason}
                                                supplierName={companyName}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
};

export { RequestRevisionDrawer };
